import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../api/client';
import LoadingOverlay from '../components/LoadingOverlay';
import './AppPage.css';
import './EmployeesPage.css';
import EmployeeDocuments from '../components/EmployeeDocuments';
import EmployeeLeaveHistory from '../components/EmployeeLeaveHistory';
import EmployeeTimeline from '../components/EmployeeTimeline';

type Catalog = { id: string; name: string };
type Catalogs = {
  positions: Catalog[];
  sectors: Catalog[];
  branches: Catalog[];
  laborCategories: Catalog[];
};
type Employee = Record<string, any> & {
  id: string;
  firstName: string;
  lastName: string;
  dni: string;
  cuil: string;
  legajo: string | null;
  status: string;
  seniority: { years: number; months: number };
};
const blank: any = {
  firstName: '',
  lastName: '',
  dni: '',
  cuil: '',
  legajo: '',
  birthDate: '',
  address: '',
  locality: '',
  postalCode: '',
  phone: '',
  email: '',
  maritalStatus: '',
  emergencyContact: '',
  emergencyPhone: '',
  healthInsurance: '',
  bankDetails: '',
  internalNotes: '',
  hireDate: '',
  terminationDate: '',
  status: 'ACTIVE',
  positionId: '',
  sectorId: '',
  branchId: '',
  laborCategoryId: '',
  agreement: '',
  contractType: '',
  workday: '',
  schedule: '',
  directManager: '',
};
const labels: any = {
  firstName: 'Nombre',
  lastName: 'Apellido',
  dni: 'DNI',
  cuil: 'CUIL',
  legajo: 'N.º de legajo',
  birthDate: 'Fecha de nacimiento',
  address: 'Domicilio',
  locality: 'Localidad',
  postalCode: 'Código postal',
  phone: 'Teléfono',
  email: 'Correo electrónico',
  maritalStatus: 'Estado civil',
  emergencyContact: 'Contacto de emergencia',
  emergencyPhone: 'Teléfono de emergencia',
  healthInsurance: 'Obra social',
  bankDetails: 'Datos bancarios',
  internalNotes: 'Observaciones internas',
  hireDate: 'Fecha de ingreso',
  terminationDate: 'Fecha de egreso',
  agreement: 'Convenio',
  contractType: 'Tipo de contratación',
  workday: 'Jornada laboral',
  schedule: 'Horario',
  directManager: 'Responsable directo',
};
const statuses: any = {
  ACTIVE: 'Activo',
  LEAVE: 'Licencia',
  VACATION: 'Vacaciones',
  SUSPENDED: 'Suspendido',
  INACTIVE: 'Baja',
};
function EmployeePhoto({ employee }: { employee: Employee }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let url = '';
    if (employee.photoUrl)
      void apiClient.get(employee.photoUrl, { responseType: 'blob' }).then((r) => {
        url = URL.createObjectURL(r.data);
        setSrc(url);
      });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [employee.id, employee.photoUrl]);
  return src ? <img src={src} alt="Fotografía" /> : <span>Sin foto</span>;
}
export default function EmployeesPage() {
  const nav = useNavigate(),
    [searchParams] = useSearchParams(),
    [items, setItems] = useState<Employee[]>([]),
    [catalogs, setCatalogs] = useState<Catalogs>({
      positions: [],
      sectors: [],
      branches: [],
      laborCategories: [],
    }),
    [form, setForm] = useState<any>(blank),
    [editing, setEditing] = useState<string | null>(null),
    [showForm, setShowForm] = useState(false),
    [view, setView] = useState<Employee | null>(null),
    [filters, setFilters] = useState<any>({
      search: '',
      status: '',
      positionId: '',
      sectorId: '',
      branchId: '',
    }),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(''),
    [msg, setMsg] = useState(''),
    [error, setError] = useState('');
  const load = async () => {
    setLoading(true);
    try {
      const p = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      setItems((await apiClient.get('/employees', { params: p })).data);
    } catch {
      setError('No se pudieron cargar los legajos.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void apiClient.get('/employees/catalogs/options').then((r) => setCatalogs(r.data));
  }, []);
  useEffect(() => {
    const employeeId = searchParams.get('employee');
    if (employeeId)
      void apiClient
        .get(`/employees/${employeeId}`)
        .then((response) => {
          setView(response.data);
          setShowForm(false);
        })
        .catch(() => setError('No se pudo abrir el legajo seleccionado.'));
  }, [searchParams]);
  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [filters]);
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('Guardando legajo…');
    setError('');
    try {
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v || undefined]));
      if (editing) await apiClient.put(`/employees/${editing}`, payload);
      else await apiClient.post('/employees', payload);
      setForm(blank);
      setEditing(null);
      setShowForm(false);
      await load();
      setMsg('Legajo guardado correctamente.');
    } catch (e: any) {
      setError(e.response?.data?.message || 'No se pudo guardar.');
    } finally {
      setBusy('');
    }
  };
  const edit = (x: Employee) => {
    setEditing(x.id);
    setShowForm(true);
    setView(null);
    setForm(
      Object.fromEntries(
        Object.keys(blank).map((k) => [
          k,
          (k.includes('Date') && x[k] ? String(x[k]).slice(0, 10) : x[k]) ?? '',
        ])
      )
    );
  };
  const remove = async (x: Employee) => {
    if (!confirm(`¿Dar de baja lógica a ${x.firstName} ${x.lastName}?`)) return;
    setBusy('Procesando baja…');
    try {
      await apiClient.delete(`/employees/${x.id}`);
      await load();
      setMsg('Empleado dado de baja.');
    } catch (e: any) {
      setError(e.response?.data?.message || 'No se pudo procesar.');
    } finally {
      setBusy('');
    }
  };
  const enableAccess = async (x: Employee) => {
    if (!x.email) {
      setError('El empleado necesita un correo electrónico.');
      return;
    }
    setBusy('Habilitando acceso…');
    try {
      await apiClient.post(`/employees/${x.id}/access`, {});
      await load();
      setMsg('Acceso habilitado. Enviamos un correo para que el empleado cree su contraseña.');
    } catch (e: any) {
      setError(e.response?.data?.message || 'No se pudo habilitar el acceso.');
    } finally {
      setBusy('');
    }
  };
  const upload = async (x: Employee, file: File) => {
    const data = new FormData();
    data.append('photo', file);
    setBusy('Subiendo fotografía…');
    try {
      await apiClient.post(`/employees/${x.id}/photo`, data);
      const fresh = (await apiClient.get(`/employees/${x.id}`)).data;
      setView(fresh);
      await load();
    } catch {
      setError('No se pudo subir la fotografía.');
    } finally {
      setBusy('');
    }
  };
  const field = (k: string, type = 'text') => (
    <label key={k}>
      {labels[k]}
      <input
        type={type}
        value={form[k]}
      required={['firstName', 'lastName', 'dni', 'cuil', 'hireDate'].includes(k)}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      />
    </label>
  );
  const select = (k: string, title: string, data: Catalog[]) => (
    <label>
      {title}
      <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}>
        <option value="">Sin especificar</option>
        {data.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <div className="app-page">
      {busy && <LoadingOverlay title={busy} />}
      <header className="app-bar">
        <button className="back-button" onClick={() => nav('/dashboard')}>
          ← Volver
        </button>
        <h1>Legajos digitales</h1>
        <button
          className="primary-button"
          onClick={() => {
            setEditing(null);
            setView(null);
            setForm(blank);
            setShowForm(true);
          }}
        >
          Nuevo empleado
        </button>
      </header>
      <main className="app-main">
        <div className="page-heading">
          <div>
            <span className="eyebrow">Capital humano</span>
            <h2>Legajos</h2>
            <p>Ficha personal y laboral completa de cada empleado.</p>
          </div>
          <div className="summary-pill">
            <strong>{items.length}</strong>
            <span>resultados</span>
          </div>
        </div>
        {error && <div className="error-banner">{error}</div>}
        {msg && <div className="success-banner">{msg}</div>}
        <section className="panel filters">
          <input
            placeholder="Buscar nombre, DNI, CUIL o legajo"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">Todos los estados</option>
            {Object.entries(statuses).map(([v, l]) => (
              <option value={v}>{l as string}</option>
            ))}
          </select>
          {[
            ['positionId', 'Puestos', catalogs.positions],
            ['sectorId', 'Sectores', catalogs.sectors],
            ['branchId', 'Sucursales', catalogs.branches],
          ].map(([k, n, d]: any) => (
            <select
              value={filters[k]}
              onChange={(e) => setFilters({ ...filters, [k]: e.target.value })}
            >
              <option value="">{n}</option>
              {d.map((x: Catalog) => (
                <option value={x.id}>{x.name}</option>
              ))}
            </select>
          ))}
        </section>
        {view ? (
          <section className="panel dossier">
            <div className="panel-heading">
              <div>
                <h2>
                  {view.firstName} {view.lastName}
                </h2>
                <p>
                  {view.legajo ? `Legajo ${view.legajo}` : 'Sin legajo'} · Antigüedad:{' '}
                  {view.seniority.years} años,{' '}
                  {view.seniority.months} meses
                </p>
              </div>
              <button className="secondary-button" onClick={() => edit(view)}>
                Editar
              </button>
            </div>
            <div className="profile">
              <label className="photo">
                <EmployeePhoto employee={view} />
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => e.target.files?.[0] && void upload(view, e.target.files[0])}
                />
              </label>
              <div className="detail-grid">
                {Object.entries(labels).map(([k, l]) => (
                  <div key={k}>
                    <small>{l as string}</small>
                    <strong>{String(view[k] ?? '—').slice(0, 10)}</strong>
                  </div>
                ))}
              </div>
              <EmployeeDocuments employeeId={view.id} onBusy={setBusy} onError={setError} />
              <EmployeeLeaveHistory employeeId={view.id} />
              <EmployeeTimeline employeeId={view.id} />
            </div>
          </section>
        ) : showForm ? (
          <section className="panel">
            <div className="panel-heading">
              <div>
                <h2>{editing ? 'Modificar legajo' : 'Alta de empleado'}</h2>
                <p>Los campos marcados por el navegador como obligatorios deben completarse.</p>
              </div>
            </div>
            <form className="form-grid" onSubmit={save}>
              <h3>Datos personales</h3>
              {[
                'firstName',
                'lastName',
                'dni',
                'cuil',
                'birthDate',
                'address',
                'locality',
                'postalCode',
                'phone',
                'email',
                'maritalStatus',
                'emergencyContact',
                'emergencyPhone',
                'healthInsurance',
                'bankDetails',
                'internalNotes',
              ].map((k) =>
                field(k, k === 'email' ? 'email' : k.includes('Date') ? 'date' : 'text')
              )}
              <h3>Datos laborales</h3>
              {field('legajo')}
              {field('hireDate', 'date')}
              {field('terminationDate', 'date')}
              <label>
                Estado
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {Object.entries(statuses).map(([v, l]) => (
                    <option value={v}>{l as string}</option>
                  ))}
                </select>
              </label>
              {select('positionId', 'Puesto', catalogs.positions)}
              {select('sectorId', 'Sector', catalogs.sectors)}
              {select('branchId', 'Sucursal o lugar de trabajo', catalogs.branches)}
              {select('laborCategoryId', 'Categoría laboral', catalogs.laborCategories)}
              {['agreement', 'contractType', 'workday', 'schedule', 'directManager'].map((k) =>
                field(k)
              )}
              <div className="form-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setEditing(null);
                    setForm(blank);
                    setShowForm(false);
                  }}
                >
                  Cancelar
                </button>
                <button className="primary-button">Guardar legajo</button>
              </div>
            </form>
          </section>
        ) : null}
        <section className="panel">
          <div className="panel-heading">
            <div>
              <h2>Directorio</h2>
              <p>Seleccioná un empleado para consultar su ficha.</p>
            </div>
          </div>
          {loading ? (
            <div className="page-message">Cargando…</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Empleado</th>
                    <th>DNI / CUIL</th>
                    <th>Puesto / sector</th>
                    <th>Estado</th>
                    <th>Antigüedad</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((x) => (
                    <tr>
                      <td>
                        <strong>
                          {x.firstName} {x.lastName}
                        </strong>
                        <br />
                        <small>{x.legajo ? `Legajo ${x.legajo}` : 'Sin legajo'}</small>
                      </td>
                      <td>
                        {x.dni}
                        <br />
                        <small>{x.cuil}</small>
                      </td>
                      <td>
                        {x.position?.name || '—'}
                        <br />
                        <small>{x.sector?.name || '—'}</small>
                      </td>
                      <td>
                        <span className="status signed">{statuses[x.status] || x.status}</span>
                      </td>
                      <td>
                        {x.seniority.years}a {x.seniority.months}m
                      </td>
                      <td className="row-actions">
                        <button
                          className="secondary-button"
                          onClick={() => {
                            setShowForm(false);
                            setView(x);
                          }}
                        >
                          Ver
                        </button>
                        <button className="secondary-button" onClick={() => edit(x)}>
                          Editar
                        </button>
                        <button className="primary-button" onClick={() => void enableAccess(x)}>
                          {x.hasAccess ? 'Restablecer clave' : 'Habilitar acceso'}
                        </button>
                        <button className="danger-button" onClick={() => void remove(x)}>
                          Baja
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
